/**
 * Region drawer + "lift region out of globe" overlay subsystem.
 *
 * Extracted verbatim from the globe client monolith (Phase 2, PR-B). It talks
 * to the globe through the window.globe seam and to the flat map through the
 * injected mapboxOverlay; it owns the window.openDrawer / openModal /
 * closeDrawer global API. Typed against the window.globe (GlobeApi) seam.
 */
/* eslint-disable */
import { MOTION_DURATIONS_MS } from '../../../views/motion'
import { getDataType } from '../data-type'
import type { MapboxOverlay } from '../overlay/mapbox'
import { prefersReducedMotion } from '../reduced-motion'

export function initDrawer(deps: { mapboxOverlay: MapboxOverlay }): void {
  const mapboxOverlay = deps.mapboxOverlay

    // Drawer handling
    var drawer = document.getElementById('region-drawer');
    var drawerContentWrapper = document.getElementById('drawer-content-wrapper');
    var drawerResizeHandle = document.getElementById('drawer-resize-handle');
    var globeWrapper = document.querySelector('.globe-wrapper') as HTMLElement | null;
    var savedSpinSpeed = 0.2;
    var savedKeyLightOrbit = false;
    var TRANSITION_DURATION = MOTION_DURATIONS_MS.slow;

    // Drive the globe width via the same --active-drawer-width custom property the
    // resize handle uses, so #globe-container keeps its proven
    // `flex: none; width: calc(100% - var(...))` layout (no flex/auto edge cases).
    function setDrawerWidthVar(px) {
      if (!globeWrapper) return;
      if (px == null) {
        globeWrapper.style.removeProperty('--active-drawer-width');
      } else {
        globeWrapper.style.setProperty('--active-drawer-width', px + 'px');
      }
    }

    // Animate the drawer-width var from `fromPx` to `toPx`. The tween is driven by
    // the globe's own render loop (startStyleTween), so each width change +
    // handleResize lands immediately before that frame's render — the same
    // ordering that keeps manual drag-resize visible. A standalone rAF here would
    // race the render loop and blank the canvas mid-transition.
    function tweenGlobeWidth(fromPx, toPx, onDone) {
      // No live globe (e.g. drawer opened from table view before init) or no
      // motion: let onDone settle the final width; there is nothing to animate.
      if (!window.globe || prefersReducedMotion() || fromPx === toPx) {
        if (typeof onDone === 'function') onDone();
        if (window.globe) {
          window.globe.handleResize();
          window.globe.needsRender = true;
        }
        return;
      }
      window.globe.startStyleTween(
        'drawerWidth',
        fromPx,
        toPx,
        function (v) {
          setDrawerWidthVar(v);
          if (window.globe) window.globe.handleResize();
        },
        { duration: TRANSITION_DURATION, onComplete: onDone }
      );
    }

    // Open: add the split-view class and animate the globe from full width down
    // to its docked width. Mobile shows a full-width drawer overlay, so there is
    // no globe width to animate there.
    function openGlobeForDrawer() {
      if (isMobileViewport()) {
        globeWrapper.classList.add('drawer-open');
        if (window.globe) {
          window.globe.handleResize();
          window.globe.needsRender = true;
        }
        return;
      }
      var targetPx = Math.round(globeWrapper.clientWidth * 0.5);
      setDrawerWidthVar(0);
      globeWrapper.classList.add('drawer-open');
      // Rest at the responsive 50% default rather than a frozen pixel width.
      tweenGlobeWidth(0, targetPx, function () {
        setDrawerWidthVar(null);
      });
    }

    // Close: animate the globe back to full width, then drop the split-view class
    // and clear the width override.
    function closeGlobeForDrawer() {
      if (!globeWrapper || !globeWrapper.classList.contains('drawer-open')) return;
      if (isMobileViewport()) {
        globeWrapper.classList.remove('drawer-open');
        setDrawerWidthVar(null);
        if (window.globe) {
          window.globe.handleResize();
          window.globe.needsRender = true;
        }
        return;
      }
      var custom = globeWrapper.style.getPropertyValue('--active-drawer-width');
      var startPx = custom ? parseFloat(custom) : Math.round(globeWrapper.clientWidth * 0.5);
      setDrawerWidthVar(startPx);
      tweenGlobeWidth(startPx, 0, function () {
        globeWrapper.classList.remove('drawer-open');
        setDrawerWidthVar(null);
      });
    }

    // Lifted state overlay elements
    var liftedOverlay = document.getElementById('lifted-state-overlay');
    var liftedStateName = document.getElementById('lifted-state-name');
    var liftedStateContent = document.getElementById('lifted-state-content');
    
    // Morph overlay elements
    var morphOverlay = document.getElementById('morph-overlay');
    var morphPath = document.getElementById('morph-path');
    var morphSvg = document.getElementById('morph-svg');
    
    // Rectangle path for the target shape (480x480 with small border radius effect)
    var RECT_PATH = 'M0,0 L480,0 L480,480 L0,480 Z';
    
    // Animation state
    var morphAnimationId = null;
    var currentMorphState = null; // 'opening', 'open', 'closing', null
    
    // Easing functions for smooth animation
    // easeInOutQuint - smooth, elegant, no bounce (best for refined transitions)
    function easeInOutQuint(t) {
      return t < 0.5 ? 16 * t * t * t * t * t : 1 - Math.pow(-2 * t + 2, 5) / 2;
    }
    
    // easeOutQuart - smooth deceleration (good for settling into position)
    function easeOutQuart(t) {
      return 1 - Math.pow(1 - t, 4);
    }

    // Helper: set up Mapbox map content and loading behavior
    function setupMapContent(mapEmbed) {
      if (!liftedStateContent || !mapEmbed) return;
      mapboxOverlay.destroy();
      liftedStateContent.innerHTML = mapEmbed.html;
      var container = document.getElementById('mapbox-container');
      if (container) {
        mapboxOverlay.initMap(container, mapEmbed.name, mapEmbed.code, mapEmbed.zoom, mapEmbed.isState);
      }
    }

    function isMobileViewport() {
      return window.innerWidth <= 768;
    }

    // Function to show lifted overlay with map embed (called after morph completes)
    function revealLiftedOverlay(name, mapEmbed) {
      if (isMobileViewport()) return;
      if (mapEmbed && liftedOverlay && liftedStateName && liftedStateContent) {
        liftedStateName.textContent = name;
        setupMapContent(mapEmbed);
        liftedOverlay.classList.add('visible');
      }
    }

    // Update lifted overlay content in-place (skip morph when already open)
    function updateLiftedOverlayContent(name, mapEmbed) {
      if (!liftedStateName || !liftedStateContent || !mapEmbed) return;
      liftedStateName.textContent = name;
      setupMapContent(mapEmbed);
    }
    
    // Function to show lifted state overlay (for US states)
    function showLiftedState(stateCode) {
      var mapEmbed = mapboxOverlay.getStateMapEmbed(stateCode);
      var name = mapboxOverlay.getStateName(stateCode);
      
      // Use the same rectangular morph animation as countries
      animateRegionOpen(name, mapEmbed);
    }

    // Function to show lifted region overlay (for countries) - uses simple animation
    function showLiftedRegion(regionCode) {
      var mapEmbed = mapboxOverlay.getRegionMapEmbed(regionCode);
      var name = mapboxOverlay.getRegionName(regionCode);
      // Use rectangle morph animation for countries (morphs from small rect to full rect)
      animateRegionOpen(name, mapEmbed);
    }
    
    // Animate region overlay with smooth scale animation (for countries without SVG paths)
    function animateRegionOpen(name, mapEmbed) {
      if (!morphOverlay || !morphPath || !liftedOverlay) {
        revealLiftedOverlay(name, mapEmbed);
        return;
      }

      // If the overlay is already open and visible, update content in-place
      // instead of replaying the morph animation.
      if (currentMorphState === 'open' && liftedOverlay.classList.contains('visible')) {
        updateLiftedOverlayContent(name, mapEmbed);
        return;
      }
      
      currentMorphState = 'opening';
      
      if (morphAnimationId) {
        cancelAnimationFrame(morphAnimationId);
      }
      
      // Position morph overlay at center
      var container = document.getElementById('globe-container');
      var containerRect = container.getBoundingClientRect();
      var centerX = containerRect.width / 2 - 240;
      var centerY = containerRect.height / 2 - 240;
      
      morphOverlay.style.left = centerX + 'px';
      morphOverlay.style.top = centerY + 'px';
      morphOverlay.style.width = '480px';
      morphOverlay.style.height = '480px';
      
      // Start with a small centered rectangle
      var startPath = 'M200,200 L280,200 L280,280 L200,280 Z';
      morphPath.setAttribute('d', startPath);
      morphPath.setAttribute('transform', '');
      morphPath.classList.remove('morphing-to-rect');
      morphOverlay.classList.add('active');
      
      // Create interpolator from small rect to full rect
      var interpolator;
      try {
        interpolator = window.flubberInterpolate(startPath, RECT_PATH, { maxSegmentLength: 10 });
      } catch (e) {
        morphOverlay.classList.remove('active');
        revealLiftedOverlay(name, mapEmbed);
        return;
      }
      
      var duration = 600;
      var startTime = performance.now();
      
      function tick(now) {
        var elapsed = now - startTime;
        var progress = Math.min(elapsed / duration, 1);
        var easedProgress = easeInOutQuint(progress);
        
        var currentPath = interpolator(easedProgress);
        morphPath.setAttribute('d', currentPath);
        
        if (progress > 0.4 && !morphPath.classList.contains('morphing-to-rect')) {
          morphPath.classList.add('morphing-to-rect');
        }
        
        if (progress < 1) {
          morphAnimationId = requestAnimationFrame(tick);
        } else {
          morphOverlay.classList.remove('active');
          morphPath.setAttribute('d', '');
          currentMorphState = 'open';
          revealLiftedOverlay(name, mapEmbed);
        }
      }
      
      morphAnimationId = requestAnimationFrame(tick);
    }

    // Function to hide lifted overlay with reverse animation
    function hideLiftedOverlay() {
      // Cancel any ongoing animation
      if (morphAnimationId) {
        cancelAnimationFrame(morphAnimationId);
        morphAnimationId = null;
      }
      
      // If overlay isn't visible or no morph elements, just hide immediately
      if (!liftedOverlay || !liftedOverlay.classList.contains('visible') || !morphOverlay || !morphPath || !window.flubberInterpolate) {
        cleanupHiddenOverlay();
        return;
      }
      
      currentMorphState = 'closing';
      
      // Get the current position of the lifted overlay in viewport coordinates
      var overlayRect = liftedOverlay.getBoundingClientRect();
      var startViewportX = overlayRect.left;
      var startViewportY = overlayRect.top;
      
      // Hide the lifted overlay content immediately
      liftedOverlay.classList.remove('visible');
      
      // Set up morph overlay
      morphOverlay.style.width = '480px';
      morphOverlay.style.height = '480px';
      
      // Start with full rectangle, animate to tiny rectangle (scales down more)
      var endPath = 'M225,225 L255,225 L255,255 L225,255 Z'; // 30x30 centered
      morphPath.setAttribute('d', RECT_PATH);
      morphPath.setAttribute('transform', '');
      morphPath.style.opacity = '1'; // Start fully visible
      morphPath.classList.add('morphing-to-rect'); // Start with rect style
      morphOverlay.classList.add('active');
      
      // Create interpolator from full rect to small rect
      var interpolator;
      try {
        interpolator = window.flubberInterpolate(RECT_PATH, endPath, { maxSegmentLength: 10 });
      } catch (e) {
        cleanupHiddenOverlay();
        return;
      }
      
      var duration = 650; // Longer duration for elegant close
      var startTime = performance.now();
      var container = document.getElementById('globe-container');
      
      function tick(now) {
        var elapsed = now - startTime;
        var progress = Math.min(elapsed / duration, 1);
        
        // Use different easing for position vs shape for more organic feel
        var positionProgress = easeOutQuart(progress);  // Smooth deceleration for movement
        var shapeProgress = easeInOutQuint(progress);   // Smooth overall for shape morph
        
        // Get current container position (changes as drawer closes)
        var containerRect = container.getBoundingClientRect();
        var targetX = containerRect.left + containerRect.width / 2 - 240;
        var targetY = containerRect.top + containerRect.height / 2 - 240;
        
        // Interpolate position from start to current center
        var currentViewportX = startViewportX + (targetX - startViewportX) * positionProgress;
        var currentViewportY = startViewportY + (targetY - startViewportY) * positionProgress;
        
        // Convert to container-relative position
        var currentX = currentViewportX - containerRect.left;
        var currentY = currentViewportY - containerRect.top;
        
        morphOverlay.style.left = currentX + 'px';
        morphOverlay.style.top = currentY + 'px';
        
        var currentPath = interpolator(shapeProgress);
        morphPath.setAttribute('d', currentPath);
        
        // Fade out and blur as it shrinks (start at 30%, complete by end)
        var fadeProgress = Math.max(0, (progress - 0.3) / 0.7);
        var easedFade = easeInOutQuint(fadeProgress);
        var opacity = 1 - easedFade;
        var blurAmount = easedFade * 8; // 0px to 8px blur
        morphPath.style.opacity = opacity.toString();
        morphPath.style.filter = 'blur(' + blurAmount + 'px)';
        
        if (progress < 1) {
          morphAnimationId = requestAnimationFrame(tick);
        } else {
          // Animation complete - cleanup
          morphPath.style.opacity = '1'; // Reset for next use
          morphPath.style.filter = ''; // Reset blur
          cleanupHiddenOverlay();
        }
      }
      
      morphAnimationId = requestAnimationFrame(tick);
    }
    
    // Helper to clean up overlay state after hiding
    function cleanupHiddenOverlay() {
      mapboxOverlay.destroy();

      if (morphOverlay) {
        morphOverlay.classList.remove('active');
        if (morphPath) {
          morphPath.setAttribute('d', '');
          morphPath.classList.remove('morphing-to-rect');
        }
      }

      if (liftedOverlay) {
        liftedOverlay.classList.remove('visible');
        // Reset position when hiding
        liftedOverlay.style.left = '50%';
        liftedOverlay.style.top = '50%';
        liftedOverlay.style.transform = 'translate(-50%, -50%)';
      }

      currentMorphState = null;
    }

    // Lifted state close button
    var liftedCloseBtn = document.getElementById('lifted-state-close');
    if (liftedCloseBtn) {
      liftedCloseBtn.addEventListener('click', function(e) {
        e.stopPropagation(); // Prevent triggering drag
        hideLiftedOverlay();
      });
    }

    // Lifted state overlay drag functionality
    var liftedHeader = document.querySelector('.lifted-state-header');
    var isDraggingOverlay = false;
    var dragStartX = 0;
    var dragStartY = 0;
    var overlayStartX = 0;
    var overlayStartY = 0;

    if (liftedHeader && liftedOverlay) {
      liftedHeader.addEventListener('mousedown', function(e) {
        isDraggingOverlay = true;
        liftedOverlay.classList.add('dragging');
        
        // Get current position
        var rect = liftedOverlay.getBoundingClientRect();
        var containerRect = liftedOverlay.parentElement.getBoundingClientRect();
        
        // Remove transform and set absolute position
        liftedOverlay.style.transform = 'none';
        liftedOverlay.style.left = (rect.left - containerRect.left) + 'px';
        liftedOverlay.style.top = (rect.top - containerRect.top) + 'px';
        
        dragStartX = (e as MouseEvent).clientX;
        dragStartY = (e as MouseEvent).clientY;
        overlayStartX = rect.left - containerRect.left;
        overlayStartY = rect.top - containerRect.top;
        
        e.preventDefault();
      });

      document.addEventListener('mousemove', function(e) {
        if (!isDraggingOverlay) return;
        
        var deltaX = e.clientX - dragStartX;
        var deltaY = e.clientY - dragStartY;
        
        liftedOverlay.style.left = (overlayStartX + deltaX) + 'px';
        liftedOverlay.style.top = (overlayStartY + deltaY) + 'px';
      });

      document.addEventListener('mouseup', function() {
        if (isDraggingOverlay) {
          isDraggingOverlay = false;
          liftedOverlay.classList.remove('dragging');
        }
      });
    }

    function isStateRegionUrl(url) {
      return url.indexOf('/country/us/region/') === 0;
    }

    function isDrawerRegionUrl(url) {
      return url.indexOf('/region/') === 0 || url.indexOf('/country/') === 0 && url.indexOf('/region/') !== -1;
    }

    // Intercept internal links inside the drawer so they open in-drawer rather than navigating
    function patchDrawerLinks(wrapper) {
      wrapper.querySelectorAll('a[href^="/"]').forEach(function(link) {
        var href = link.getAttribute('href');
        if (href === '/' || href.startsWith('/country/')) {
          link.onclick = function(e) {
            e.preventDefault();
            closeDrawer();
          };
        } else if (isDrawerRegionUrl(href)) {
          link.onclick = function(e) {
            e.preventDefault();
            var code = href.split('/').pop().toUpperCase();
            window.openDrawer(href, code);
          };
        }
      });

      // Add click-to-scroll for drawer tab labels
      var tabs = wrapper.querySelectorAll('.drawer-tab-static');
      tabs.forEach(function(tab) {
        tab.style.cursor = 'pointer';
        tab.addEventListener('click', function() {
          var isResources = tab.classList.contains('drawer-tab-resources');
          var scrollContainer = wrapper.closest('#region-drawer') || wrapper.parentElement;
          if (scrollContainer) {
            if (isResources) {
              var panel = wrapper.querySelector('#panel-resources');
              if (panel) scrollContainer.scrollTo({ top: panel.offsetTop, behavior: 'smooth' });
            } else {
              scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
            }
          }
        });
      });
    }

    // Parse a fetched detail document down to the inner content node the drawer
    // renders. Shared by every drawer-open path so the .modal-content/.content-wrapper
    // lookup stays consistent.
    function parseDrawerContent(html) {
      var parser = new DOMParser();
      var doc = parser.parseFromString(html, 'text/html');
      return doc.querySelector('.modal-content') || doc.querySelector('.content-wrapper');
    }

    // Dock the globe for an open drawer: stop spin/orbit (saving prior state once)
    // and center on the region. Shared by the open, popstate, and deep-link paths.
    function dockGlobeForDrawer(countryCode, regionType, onCentered?) {
      if (!window.globe) return false;
      if (!drawer.classList.contains('open')) {
        savedSpinSpeed = window.globe.spinSpeed;
        savedKeyLightOrbit = window.globe.keyLightOrbit;
      }
      window.globe.spinSpeed = 0;
      window.globe.keyLightOrbit = false;
      window.globe.drawerOpen = true;
      if (countryCode) window.globe.centerOnCountry(countryCode, regionType, onCentered);
      return true;
    }

    // Function to open drawer with content - make it global so Three.js module can access it
    window.openDrawer = function openDrawer(url, countryCode) {
      var wasOpen = globeWrapper.classList.contains('drawer-open');

      // Lock body scroll so the outer page container doesn't capture scroll gestures
      document.body.style.overflow = 'hidden';

      // Add the split-view class and smoothly shrink the globe. Skip the animation
      // when the drawer is already open (navigating between regions in-drawer must
      // not re-run the open transition).
      if (!wasOpen) {
        openGlobeForDrawer();
      } else if (window.globe) {
        window.globe.handleResize();
      }
      
      // Pause spinning, key light orbit, and center on country. Region type is
      // derived from the URL to avoid code collisions (e.g. AR = Argentina vs
      // Arkansas); the lifted overlay (map panel) is deferred until the globe is
      // near-centered.
      var tableViewActive = document.getElementById('filter-table-view-btn') && document.getElementById('filter-table-view-btn').classList.contains('active');
      var isStateRegion = isStateRegionUrl(url);
      var globeDocked = dockGlobeForDrawer(countryCode, isStateRegion ? 'state' : 'country', function() {
        // Show map overlay only when globe rotation is near-complete
        if (tableViewActive) {
          hideLiftedOverlay();
        } else if (isStateRegion) {
          showLiftedState(countryCode);
        } else if (url.startsWith('/region/')) {
          showLiftedRegion(countryCode);
        }
      });
      if (!globeDocked) {
        // No globe available — show lifted overlay immediately (or hide if in table view)
        if (tableViewActive) {
          hideLiftedOverlay();
        } else if (isStateRegion && countryCode) {
          showLiftedState(countryCode);
        } else if (url.startsWith('/region/') && countryCode) {
          showLiftedRegion(countryCode);
        } else {
          hideLiftedOverlay();
        }
      }
      
      // Determine current data type (Age Assurance vs Digital IDs)
      var dataType = getDataType();

      function setDrawerContentAndOpen(html, queryUrl, drawerUrl) {
        // Strip the outer .modal-content wrapper so only .detail-layout lands in
        // .drawer-content — matching the age-assurance and popstate paths. The
        // globe page styles .modal-content for the standalone modal overlay
        // (margin-top, min-height, max-width), which busts the drawer layout.
        var content = parseDrawerContent(html);
        drawerContentWrapper.innerHTML = content ? content.innerHTML || content.outerHTML : html;
        drawer.classList.add('open');
        history.pushState({ drawerUrl: drawerUrl || url, code: countryCode }, '', queryUrl);
        patchDrawerLinks(drawerContentWrapper);
      }

      if (dataType === 'digital_ids') {
        // Digital IDs mode: fetch drawer HTML (same layout as Age Assurance)
        var digitalIdsCode = isStateRegionUrl(url) ? 'us' : (countryCode || url.split('/').pop()).toLowerCase();
        var htmlUrl = '/api/digital-ids/regions/' + digitalIdsCode + '/html';
        fetch(htmlUrl)
          .then(function(res) {
            return res.text();
          })
          .then(function(html) {
            setDrawerContentAndOpen(html, '/?region=' + digitalIdsCode + '&data=digital_ids', htmlUrl);
          })
          .catch(function(err) {
            console.error('Error loading digital IDs drawer content:', err);
            var errHtml = '<div class="modal-content"><div class="detail-layout"><a href="/" class="back-link">← Back</a><div class="detail-header"><h1 class="region-name">Digital IDs</h1></div><div class="section"><div class="section-content"><p class="text-sm text-secondary">Unable to load coverage for this region.</p></div></div></div></div>';
            setDrawerContentAndOpen(errHtml, '/?region=' + digitalIdsCode, null);
          });
        return;
      }

      // Age Assurance: fetch HTML from existing region/state routes
      fetch(url, { headers: { 'X-Atlas-Drawer': '1' } })
        .then(function(response) {
          return response.text();
        })
        .then(function(html) {
          var content = parseDrawerContent(html);

          if (content) {
            var existingCloseBtn = content.querySelector('.modal-close');
            if (existingCloseBtn) {
              existingCloseBtn.remove();
            }
            drawerContentWrapper.innerHTML = content.innerHTML || content.outerHTML;
            drawer.classList.add('open');
            var urlType = isStateRegionUrl(url) ? 'state' : 'region';
            var queryUrl = '/?' + urlType + '=' + countryCode.toLowerCase();
            history.pushState({ drawerUrl: url, code: countryCode }, '', queryUrl);
            patchDrawerLinks(drawerContentWrapper);
          }
        })
        .catch(function(error) {
          console.error('Error loading drawer content:', error);
        });
    };
    
    // Alias for backward compatibility
    window.openModal = window.openDrawer;
    
    // Function to close drawer
    function closeDrawer(skipHistoryUpdate?) {
      // No-op if the drawer was never open. Callers such as the data-type mode
      // switch invoke closeDrawer() defensively; without this guard the restore
      // below would clobber live globe state (e.g. keyLightOrbit) with stale
      // saved defaults that were never captured from an actual open.
      if (!drawer.classList.contains('open')) return;

      drawer.classList.remove('open');
      document.body.style.overflow = '';

      // Hide lifted overlay
      hideLiftedOverlay();

      // Update URL back to base (unless triggered by popstate)
      if (!skipHistoryUpdate) {
        history.pushState({}, '', '/');
      }

      // Animate the globe back to full width, then drop the split-view class and
      // clear the width override (handled in closeGlobeForDrawer's onDone).
      closeGlobeForDrawer();

      // Restore globe state and reset the (possibly resized) drawer width after
      // the transition completes.
      setTimeout(function() {
        if (window.globe) {
          window.globe.spinSpeed = savedSpinSpeed;
          // The settings toggle is the source of truth for the orbit: if the
          // user changed it while the drawer was open, honor that rather than
          // restoring the snapshot captured when the drawer opened.
          var orbitToggle = document.querySelector('.settings-toggle[data-setting="key-light-orbit"]');
          window.globe.keyLightOrbit = orbitToggle
            ? orbitToggle.classList.contains('active')
            : savedKeyLightOrbit;
          window.globe.drawerOpen = false;
          window.globe.clearSelection();
        }
        drawer.style.width = '';
        drawerContentWrapper.innerHTML = '';
      }, TRANSITION_DURATION + 50);
    }
    
    window.closeDrawer = closeDrawer;

    // Logo click - go home (close drawer if open)
    var logoLink = document.getElementById('logo-home-link');
    if (logoLink) {
      logoLink.addEventListener('click', function(e) {
        e.preventDefault();
        if (drawer.classList.contains('open')) {
          closeDrawer();
        } else {
          // Already at home, no action needed
          window.location.href = '/';
        }
      });
    }

    // Escape key to close drawer
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && drawer.classList.contains('open')) {
        closeDrawer();
      }
    });
    
    // Drawer resize functionality
    var isResizing = false;
    var startX = 0;
    var startWidth = 0;
    
    drawerResizeHandle.addEventListener('mousedown', function(e) {
      isResizing = true;
      startX = e.clientX;
      startWidth = drawer.offsetWidth;
      drawerResizeHandle.classList.add('dragging');
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
      e.preventDefault();
    });
    
    document.addEventListener('mousemove', function(e) {
      if (!isResizing) return;
      
      var diff = startX - e.clientX;
      var newWidth = startWidth + diff;
      var minWidth = 300;
      var maxWidth = window.innerWidth - 200;
      
      newWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));
      drawer.style.width = newWidth + 'px';
      
      // Update drawer width CSS variable so the globe fills remaining space via CSS
      if (globeWrapper) {
        globeWrapper.style.setProperty('--active-drawer-width', newWidth + 'px');
      }
      
      // Trigger globe resize
      if (window.globe) {
        window.globe.handleResize();
        window.globe.needsRender = true;
      }
    });
    
    document.addEventListener('mouseup', function() {
      if (isResizing) {
        isResizing = false;
        drawerResizeHandle.classList.remove('dragging');
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    });
    
    // Handle browser back/forward navigation
    window.addEventListener('popstate', function(e) {
      if (e.state && e.state.drawerUrl) {
        // Navigate to a drawer URL - open drawer without pushing new history
        var url = e.state.drawerUrl;
        var countryCode = e.state.code;
        
        // Open drawer content without adding to history (we're replaying history)
        var wasOpen = globeWrapper.classList.contains('drawer-open');
        if (!wasOpen) {
          openGlobeForDrawer();
        } else if (window.globe) {
          window.globe.handleResize();
        }
        
        dockGlobeForDrawer(countryCode, isStateRegionUrl(url) ? 'state' : 'country');
        
        fetch(url, { headers: { 'X-Atlas-Drawer': '1' } })
          .then(function(response) { return response.text(); })
          .then(function(html) {
            var content = parseDrawerContent(html);
            if (content) {
              var existingCloseBtn = content.querySelector('.modal-close');
              if (existingCloseBtn) existingCloseBtn.remove();
              drawerContentWrapper.innerHTML = content.innerHTML || content.outerHTML;
              drawer.classList.add('open');
              patchDrawerLinks(drawerContentWrapper);
            }
          });
      } else {
        // Navigated back to base URL - close drawer
        if (drawer.classList.contains('open')) {
          closeDrawer(true); // Skip history update since we're responding to popstate
        }
      }
    });
    
    // Check if page loaded with a drawer URL (query params) and open it
    (function() {
      var params = new URLSearchParams(window.location.search);
      var regionCode = params.get('region');
      var stateCode = params.get('state');
      var isDigitalIds = params.get('data') === 'digital_ids';

      if (regionCode || stateCode) {
        var code = (regionCode || stateCode).toUpperCase();
        var regionType = stateCode ? 'state' : 'country';
        var fetchPath, fetchOpts;

        if (isDigitalIds) {
          // Digital IDs deep links (/?region=...&data=digital_ids) always carry a
          // country-level region code and must fetch the Digital ID drawer
          // endpoint, not the age-assurance /region/:code route. Switch the
          // content toggle so the globe, table, and filters render this mode.
          regionType = 'country';
          var digitalIdsCode = (regionCode || stateCode).toLowerCase();
          fetchPath = '/api/digital-ids/regions/' + digitalIdsCode + '/html';
          if (typeof window.setAtlasDataType === 'function') {
            window.setAtlasDataType('digital_ids');
          }
        } else {
          // Age-assurance region/state routes are drawer-only and gated on the
          // X-Atlas-Drawer header (otherwise they redirect to /).
          fetchPath = regionCode ? '/region/' + regionCode : '/country/us/region/' + stateCode;
          fetchOpts = { headers: { 'X-Atlas-Drawer': '1' } };
        }

        // Replace current history entry with proper state
        history.replaceState({ drawerUrl: fetchPath, code: code }, '', window.location.href);

        // Wait for borders to be ready before opening drawer with selection
        function waitForBordersAndOpen() {
          if (window.globe && window.globe.bordersReady) {
            // Borders are ready, open the drawer
            openGlobeForDrawer();
            dockGlobeForDrawer(code, regionType);
            
            fetch(fetchPath, fetchOpts)
              .then(function(response) { return response.text(); })
              .then(function(html) {
                var content = parseDrawerContent(html);
                if (content) {
                  var existingCloseBtn = content.querySelector('.modal-close');
                  if (existingCloseBtn) existingCloseBtn.remove();
                  drawerContentWrapper.innerHTML = content.innerHTML || content.outerHTML;
                  drawer.classList.add('open');
                  
                  // Show lifted overlay for page refresh. Digital IDs links use
                  // country-level region codes, so they always use the region
                  // overlay rather than the US-state overlay.
                  if (stateCode && !isDigitalIds) {
                    showLiftedState(code);
                  } else {
                    showLiftedRegion(code);
                  }
                  
                  patchDrawerLinks(drawerContentWrapper);
                }
              });
          } else {
            // Borders not ready yet, poll again
            setTimeout(waitForBordersAndOpen, 50);
          }
        }
        
        // Start polling
        waitForBordersAndOpen();
      }
    })();

}
